import { useState, useEffect } from 'react';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { INTERACTIVE_SERVICE_BASE_URL } from '../constants';

const ARTIFACT_API_PREFIX = INTERACTIVE_SERVICE_BASE_URL;

const requestArtifactContent = async contentUrl => {
    const res = await interactiveServiceFetch(`${ARTIFACT_API_PREFIX}/${contentUrl}`);
    if (!res.ok) throw new Error(`Failed to fetch artifact: ${res.status}`);
    return res.text();
};

// Artifact content is immutable once stored, so cache the in-flight promise by
// URL. This dedupes the renderer's lazy-load with the copy path's hydration
// (both call this) and avoids re-downloading on repeat reads. `contentUrl` is
// the relative path the backend hands out — used for reload and grid export.
const contentCache = new Map();

export const fetchArtifactContent = contentUrl => {
    if (!contentCache.has(contentUrl)) {
        const promise = requestArtifactContent(contentUrl).catch(err => {
            contentCache.delete(contentUrl); // allow retry after a failure
            throw err;
        });
        contentCache.set(contentUrl, promise);
    }
    return contentCache.get(contentUrl);
};

/**
 * Hook for lazily fetching artifact content from the backend.
 *
 * During live streaming, artifacts arrive with inline `data` — this hook
 * is a no-op when `contentUrl` is falsy.  On session reload, the backend
 * returns a `content_url` (relative backend path) instead of `data`;
 * this hook fetches the content via the authenticated backend proxy.
 */
const useArtifactContent = contentUrl => {
    const [content, setContent] = useState(null);
    const [isLoading, setIsLoading] = useState(!!contentUrl);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!contentUrl) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchArtifactContent(contentUrl)
            .then(text => {
                if (!cancelled) {
                    setContent(text);
                    setIsLoading(false);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    setError(err.message);
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [contentUrl]);

    return { content, isLoading, error };
};

export default useArtifactContent;
